import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchTransactionsStart } from "../../redux/transactions/reducer";
import {
  selectTransactionsData,
  selectIsFetchingTransactions,
  selectFilteredTransactions,
} from "../../redux/transactions/selectors";
import usePaginationWrapper from "../general/usePaginationWrapper";
import { httpGetTransactions } from "../../services/transactions";
import { errorNotifier } from "../../utils/helpers";

const useTransactionsQuery = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectIsFetchingTransactions);
  const transactions = useSelector(selectTransactionsData);
  const filteredTransactions = useSelector(selectFilteredTransactions);
  const [successfulTransactions, setSuccessfulTransactions] = useState(0);
  const [failedTransactions, setFailedTransactions] = useState(0);
  const [pendingTransactions, setPendingTransactions] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);

  const [fetch, setFetch] = useState(0);
  const refetch = () => setFetch((prev) => prev + 1);
  const {
    limit,
    offset,
    setOffset,
    setLimit,
    totalElements,
    totalPages,
    numberOfElements,
    setPageable,
    page,
    setPage,
    pageOptions,
    searchText,
    debouncedSearchText,
    setSearchText,
  } = usePaginationWrapper({ defaultLimit: 8 });

  const isFiltering = statusFilter || debouncedSearchText ? true : false;

  const fetchTransactions = useCallback(() => {
    dispatch(
      fetchTransactionsStart({
        data: {
          page: (page - 1) * limit,
          size: limit,
          username: debouncedSearchText || null,
          status: statusFilter?.value || null,
        },
        options: {
          isFiltering,
        },
      })
    );
  }, [dispatch, page, limit, debouncedSearchText, statusFilter, isFiltering]);

  useEffect(() => {
    if (transactions && !isFiltering) {
      setPageable({
        total_elements: transactions?.count,
        total_pages: transactions?.pages,
        number_of_elements: transactions?.items?.length,
      });
    }

    if (filteredTransactions && isFiltering) {
      setPageable({
        total_elements: filteredTransactions?.count,
        total_pages: filteredTransactions?.pages,
        number_of_elements: filteredTransactions?.items?.length,
      });
    }
  }, [
    transactions,
    setPageable,
    fetch,
    debouncedSearchText,
    isFiltering,
    filteredTransactions,
  ]);

  useEffect(() => {
    fetchTransactions();
    getTransactionsStatusCount("SUCCESS");
    getTransactionsStatusCount("PENDING");
    getTransactionsStatusCount("FAILED");
  }, [fetchTransactions, fetch]);

  const getTransactionsStatusCount = async (status) => {
    try {
      const response = await httpGetTransactions({
        data: { status, page: 0, size: 1 },
      });

      if (status === "SUCCESS") {
        setSuccessfulTransactions(response?.raw?.count || 0);
      }
      if (status === "PENDING") {
        setPendingTransactions(response?.raw?.count || 0);
      }
      if (status === "FAILED") {
        setFailedTransactions(response?.raw?.count || 0);
      }
    } catch (err) {
      errorNotifier("Unable to fetch successful transactions");
    }
  };

  return {
    transactions,
    loadingTransactions: isLoading,
    refetch,
    limit,
    offset,
    setOffset,
    setLimit,
    totalElements,
    totalPages,
    numberOfElements,
    setPageable,
    page,
    setPage,
    pageOptions,
    searchText,
    debouncedSearchText,
    setSearchText,
    setStatusFilter,
    statusFilter,
    filteredTransactions,
    isFiltering,
    successfulTransactions,
    pendingTransactions,
    failedTransactions,
  };
};

export default useTransactionsQuery;
